"use client";
import type { LyricsWithSlug } from "@/types";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSession } from "next-auth/react";
import Link from "../../components/Link";
import AdminLyricsReports from "../../components/lyrics/AdminLyricsReports";
import AdminLyricsLintButton from "./AdminLyricsLintButton";
import LyricsPageStats from "./AdminLyricsPageStats";
import AdminLyricsSourceLink from "./AdminLyricsSourceLink";

export default function AdminLyricsSection({ lyrics }: { lyrics: LyricsWithSlug }) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  if (!isAdmin) return null;
  if (!lyrics) return null;

  return (
    <Box sx={{
      mt: 2, p: 2,
      border: "1px solid",
      borderLeftWidth: { xs: 0, sm: 1 },
      borderRightWidth: { xs: 0, sm: 1 },
      borderColor: "warning.main",
      bgcolor: "color-mix(in srgb, var(--mui-palette-warning-main), transparent 90%)",
    }}>
      <LyricsPageStats slug={lyrics.slug} />
      <AdminLyricsSourceLink lyrics={lyrics} />

      {/* Admin-only quick metadata: keywords + searches */}
      {(lyrics.keywords?.length || lyrics.searches?.length) ? (
        <Box sx={{ mt: 1, mb: 1 }}>
          {lyrics.keywords && lyrics.keywords.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Cuvinte cheie:</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {lyrics.keywords.map((k) => (
                  <Chip key={k} label={k} size="small" sx={{ fontFamily: 'monospace' }} />
                ))}
              </Stack>
            </Box>
          )}

          {lyrics.searches && lyrics.searches.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>Căutări:</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {lyrics.searches.map((s) => (
                  <Chip key={s} label={s} size="small" sx={{ fontFamily: 'monospace' }} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <AdminLyricsLintButton slug={lyrics.slug} />
        <Link
          href={`/admin/versuri/rasfoieste?cauta=${encodeURIComponent(lyrics.slug)}`}
          target="_blank"
          size="small"
          effect="underline-on-hover"
          sx={{ mt: 1 }}
        >
          Vezi-n Admin
        </Link>
      </Box>
      <AdminLyricsReports lyrics={lyrics} />
    </Box>
  )
}

